import {compressImage} from './photoCompress.js';
import type {SupabaseClient} from './supabaseTypes.js';

export const EQUIPMENT_FUELING_PHOTO_BUCKET = 'equipment-maintenance-docs';

function safeJpegName(fileName: string | null | undefined): string {
  const stem = String(fileName || 'photo')
    .replace(/\.[^.]*$/, '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/^_+|_+$/g, '');
  return `${stem || 'photo'}.jpg`;
}

async function storageObjectExists(sb: SupabaseClient, path: string): Promise<boolean> {
  const slash = path.lastIndexOf('/');
  const folder = slash === -1 ? '' : path.slice(0, slash);
  const fileName = slash === -1 ? path : path.slice(slash + 1);
  try {
    const {data, error} = await sb.storage
      .from(EQUIPMENT_FUELING_PHOTO_BUCKET)
      .list(folder, {limit: 100, search: fileName});
    return !error && Array.isArray(data) && data.some((object) => object?.name === fileName);
  } catch {
    return false;
  }
}

// The upload failure is whatever the ambiguous outcome produced: a StorageError
// from the client, or ANY thrown value from the fetch layer — `catch` binds it
// as `unknown` and it need not be an Error at all. This reproduces the previous
// `uploadError.message || String(uploadError)` exactly: a truthy `message` wins
// and is stringified by the Error constructor, everything else falls back to
// String(value).
function uploadFailureMessage(value: unknown): string {
  if (typeof value === 'object' && value !== null && 'message' in value) {
    const {message} = value;
    if (message) return String(message);
  }
  return String(value);
}

export type EquipmentFuelingPhoto = {name: string; path: string; uploadedAt: string};

export async function uploadEquipmentFuelingPhoto(
  sb: SupabaseClient,
  equipmentSlug: string,
  file: File,
  {now = () => Date.now()}: {now?: () => number} = {},
): Promise<EquipmentFuelingPhoto> {
  if (!equipmentSlug) throw new Error('equipment slug required');
  if (!file) throw new Error('photo file required');

  const timestamp = now();
  const path = `fueling/${equipmentSlug}/${timestamp}-${safeJpegName(file.name)}`;
  const compressed = await compressImage(file);
  let uploadError: unknown = null;
  try {
    const result = await sb.storage
      .from(EQUIPMENT_FUELING_PHOTO_BUCKET)
      .upload(path, compressed, {upsert: false, contentType: 'image/jpeg'});
    uploadError = result.error;
  } catch (error) {
    uploadError = error;
  }

  // Mobile Safari can report "Load Failed" after Storage has already accepted
  // the bytes but before the success response reaches the browser. Reconcile
  // that ambiguous outcome against the exact object path before telling the
  // operator to retry (which would otherwise create an unnecessary duplicate).
  if (uploadError && !(await storageObjectExists(sb, path))) {
    throw new Error(uploadFailureMessage(uploadError));
  }

  return {
    name: file.name || 'photo',
    path,
    uploadedAt: new Date(timestamp).toISOString(),
  };
}
